import { EMBED_ORIGIN_PARAM, PROTOCOL_SOURCE, PROTOCOL_VERSION } from "./constants";
import type { IncomingMessage } from "./types";

/** Appends the loader's own origin so the iframe app knows where it's allowed to postMessage back to (section 15) — never a hardcoded Wix domain inside the app itself. */
export function buildIframeSrc(bookingUrl: string): string {
  const url = new URL(bookingUrl);
  url.searchParams.set(EMBED_ORIGIN_PARAM, window.location.origin);
  return url.toString();
}

/**
 * Section 15, security-critical: a message is only ever trusted if it
 * came from the EXACT iframe window we created (event.source), from the
 * EXACT origin we loaded into that iframe (event.origin — derived from
 * our own configured bookingUrl, never a Wix-domain guess), and carries
 * our own versioned protocol envelope. Any other combination is ignored
 * outright — never a wildcard, never "close enough".
 */
export function parseIncomingMessage(event: MessageEvent, expectedSource: Window | null, expectedOrigin: string): IncomingMessage | null {
  if (!expectedSource || event.source !== expectedSource) return null;
  if (event.origin !== expectedOrigin) return null;

  const data = event.data as unknown;
  if (typeof data !== "object" || data === null) return null;
  const msg = data as Record<string, unknown>;
  if (msg.source !== PROTOCOL_SOURCE || msg.version !== PROTOCOL_VERSION) return null;

  switch (msg.type) {
    case "READY":
      return { source: PROTOCOL_SOURCE, version: PROTOCOL_VERSION, type: "READY" };
    case "RESERVATION_COMPLETED":
      if (typeof msg.reservationId !== "string" || msg.reservationId.length === 0) return null;
      return { source: PROTOCOL_SOURCE, version: PROTOCOL_VERSION, type: "RESERVATION_COMPLETED", reservationId: msg.reservationId };
    case "CLOSE_REQUEST":
      return { source: PROTOCOL_SOURCE, version: PROTOCOL_VERSION, type: "CLOSE_REQUEST" };
    case "FLOW_STATE_CHANGED":
      if (typeof msg.dirty !== "boolean") return null;
      return { source: PROTOCOL_SOURCE, version: PROTOCOL_VERSION, type: "FLOW_STATE_CHANGED", dirty: msg.dirty };
    default:
      return null;
  }
}
